import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { Card } from '@mui/material';

import BannerTask from '../speedgrader/components/BannerTask';
import StudentTaskHeader from '../speedgrader/components/studentTask/StudentTaskHeader';
import CardCorrectTask from '../speedgrader/components/studentTask/CardCorrectTask';
import TaskRender from '../speedgrader/components/TaskRender';
import CommentsChat from '../../components/shared/presentation-comments/CommentsChat';
import { Text } from '../../components/common/Texts';
import SimpleLoading from '../../components/common/SimpleLoading';
import {
    getConversationResource,
    postCommentResource,
    editCommentResource,
} from '../../redux/api/comments';
import toast from 'react-hot-toast';
import {
    formatCommentDate,
    formatCommentDateTime,
    getCommentSortMs,
} from '../../utils/dates/dates.utils';
import {
    buildLegacyCommentsFromSubmission,
    mergeLegacyAndConversationComments,
    isAdiProfessorEmail,
} from '../../components/shared/presentation-comments/legacyComments.utils';
import { titleHTML } from '../../helpers/title.helper';
import { Img } from '../../components/common/Image';
import withoutSubmission from '../../assets/media/teacher/without-submission.png';

const pickAttachmentUrl = (v) =>
    v && typeof v === 'object' && v.url ? v.url : null;

const PresentationComments = () => {
    titleHTML('Comentarios de entrega');
    const { roomId, id } = useParams();
    const conversationId = roomId || id;

    const [conversation, setConversation] = useState(null);
    const [comments, setComments] = useState([]);
    const [comment, setComment] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const [isEditingMessage, setIsEditingMessage] = useState(false);
    const [chatMediaReset, setChatMediaReset] = useState(0);
    const [chatFile, setChatFile] = useState(null);
    const [chatAudio, setChatAudio] = useState(null);
    const [chatVideo, setChatVideo] = useState(null);

    const [leftWidthPercentage, setLeftWidthPercentage] = useState(70);
    const [backgroundColor, setBackgroundColor] = useState('#BBB');
    const [pointerEventsNone, setPointerEventsNone] = useState(false);
    const containerRef = useRef(null);
    const animationFrameRef = useRef(null);

    const [note, setNote] = useState(0);
    const [redo, setRedo] = useState(false);
    const [check, setCheck] = useState(false);

    useEffect(() => {
        const fetchConversation = async () => {
            if (!conversationId) return;
            try {
                setIsLoading(true);
                const response = await getConversationResource(conversationId);
                if (response && !response.error) {
                    setConversation(response);
                    const legacyComments = buildLegacyCommentsFromSubmission({
                        submission: response?.submission || response,
                        student: {
                            name: response.student_name,
                            image_url: response.student_picture,
                        },
                        professor: {
                            name: response.teacher_name,
                            image_url: response.teacher_picture,
                            email:
                                response.teacher_email ||
                                response.professor_email ||
                                response?.submission?.professor?.email,
                        },
                    });
                    const presentationMergeOptions = {
                        submissionId:
                            response.submission_id ??
                            response?.submission?.id ??
                            null,
                        isAdiProfessor: isAdiProfessorEmail(
                            response?.professor?.email ||
                                response?.teacher_email ||
                                response?.submission?.professor?.email
                        ),
                    };
                    if (response.messages && Array.isArray(response.messages)) {
                        const transformed = response.messages.map((item) => {
                            const att = item.attachments || {};
                            const fileUrl =
                                pickAttachmentUrl(att.file) ||
                                (item.attachment &&
                                item.attachment !== false &&
                                item.attachment.url
                                    ? item.attachment.url
                                    : null);
                            const submissionIdNum = Number(item.submission_id);
                            return {
                                id: item.id,
                                name: item.author_name,
                                comment: item.message,
                                date:
                                    formatCommentDate(item.create_date) +
                                    ' ' +
                                    formatCommentDateTime(item.create_date),
                                photo: item.author_picture || null,
                                sender: item.author_is_teacher
                                    ? 'user'
                                    : 'other',
                                author_is_teacher: item.author_is_teacher,
                                comment_type: item.comment_type || 'normal',
                                submission_id: Number.isNaN(submissionIdNum)
                                    ? null
                                    : submissionIdNum,
                                image: fileUrl,
                                attachments: {
                                    fileUrl,
                                    audioUrl: pickAttachmentUrl(att.audio),
                                    videoUrl: pickAttachmentUrl(att.video),
                                },
                            };
                        });
                        setComments(
                            mergeLegacyAndConversationComments(
                                legacyComments,
                                transformed,
                                presentationMergeOptions
                            )
                        );
                    } else {
                        setComments(
                            mergeLegacyAndConversationComments(
                                legacyComments,
                                [],
                                presentationMergeOptions
                            )
                        );
                    }
                }
            } catch (error) {
                console.error('Error al obtener la conversación:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchConversation();
    }, [conversationId]);

    const handleMouseDown = (e) => {
        const startX = e.clientX;
        const containerWidth =
            containerRef.current.getBoundingClientRect().width;
        const startLeftWidth = (leftWidthPercentage / 100) * containerWidth;

        const handleMouseMove = (e) => {
            setBackgroundColor('#c4c4c4');
            setPointerEventsNone(true);
            if (animationFrameRef.current) return;
            animationFrameRef.current = requestAnimationFrame(() => {
                const newLeftWidth = startLeftWidth + (e.clientX - startX);
                const newLeftWidthPercentage =
                    (newLeftWidth / containerWidth) * 100;
                if (newLeftWidthPercentage > 100) {
                    setLeftWidthPercentage(100);
                } else if (newLeftWidthPercentage < 0) {
                    setLeftWidthPercentage(0);
                } else {
                    setLeftWidthPercentage(newLeftWidthPercentage);
                }
                animationFrameRef.current = null;
            });
        };

        const stopResizing = () => {
            setBackgroundColor('#BBB');
            setPointerEventsNone(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', stopResizing);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', stopResizing);
    };

    const handleChatSendFile = (file) => {
        setChatFile(file || null);
    };

    const handleChatSendAudio = (blob) => {
        setChatAudio(blob || null);
    };

    const handleChatSendVideo = (blob) => {
        setChatVideo(blob || null);
    };

    const clearChatMediaDraft = () => {
        setChatFile(null);
        setChatAudio(null);
        setChatVideo(null);
        setChatMediaReset((n) => n + 1);
    };

    const handleSendMessage = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (!comment.trim()) return;
        if (!conversation) return;

        setIsSendingMessage(true);
        try {
            const formData = new FormData();
            formData.append('message', comment);
            formData.append(
                'conversation_id',
                String(parseInt(conversationId, 10))
            );
            if (chatFile instanceof File) {
                formData.append('attachment', chatFile);
            }
            if (chatAudio instanceof Blob) {
                formData.append(
                    'file_audio',
                    chatAudio,
                    chatAudio instanceof File
                        ? chatAudio.name
                        : 'audio_grabado.wav'
                );
            }
            if (chatVideo instanceof Blob) {
                formData.append(
                    'file_video',
                    chatVideo,
                    chatVideo instanceof File
                        ? chatVideo.name
                        : 'video_grabado.webm'
                );
            }

            const response = await postCommentResource(
                parseInt(conversationId, 10),
                formData
            );

            if (
                response &&
                !response.error &&
                (response.id != null || response.message_id != null)
            ) {
                const resAtt = response.attachments || {};
                const fileUrl =
                    pickAttachmentUrl(resAtt.file) ||
                    (response.attachment &&
                    response.attachment !== false &&
                    response.attachment.url
                        ? response.attachment.url
                        : null);
                const newComment = {
                    id: response.message_id || response.id,
                    name: response.author_name || '',
                    comment: response.message || comment,
                    date: response.create_date
                        ? formatCommentDate(response.create_date) +
                          ' ' +
                          formatCommentDateTime(response.create_date)
                        : new Date().toLocaleDateString('es-ES', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                          }) +
                          ' ' +
                          new Date().toLocaleTimeString('es-ES', {
                              hour: '2-digit',
                              minute: '2-digit',
                          }) +
                          ' hs',
                    photo: response.author_picture || null,
                    sender: response.author_is_teacher ? 'user' : 'other',
                    author_is_teacher: response.author_is_teacher,
                    comment_type: response.comment_type || 'normal',
                    image: fileUrl,
                    attachments: {
                        fileUrl,
                        audioUrl: pickAttachmentUrl(resAtt.audio),
                        videoUrl: pickAttachmentUrl(resAtt.video),
                    },
                    sort_ms: response.create_date
                        ? getCommentSortMs(response.create_date)
                        : Date.now(),
                };
                setComments((prev) => [...prev, newComment]);
                setComment('');
                clearChatMediaDraft();
            }
        } catch (error) {
            console.error('Error al enviar el comentario:', error);
        } finally {
            setIsSendingMessage(false);
        }
    };

    const handleEditMessage = async (messageId, nextMessage, media = {}) => {
        const trimmedMessage = nextMessage.trim();

        if (!trimmedMessage) return false;

        setIsEditingMessage(true);

        try {
            const formData = new FormData();
            formData.append('message', trimmedMessage);
            const { file, audio, video } = media || {};
            if (file instanceof File) {
                formData.append('attachment', file);
            }
            if (audio instanceof Blob) {
                formData.append(
                    'file_audio',
                    audio,
                    audio instanceof File ? audio.name : 'audio_grabado.wav'
                );
            }
            if (video instanceof Blob) {
                formData.append(
                    'file_video',
                    video,
                    video instanceof File ? video.name : 'video_grabado.webm'
                );
            }

            const response = await editCommentResource(messageId, formData);

            if (response && !response.error) {
                const resAtt = response.attachments || {};
                const updatedFile =
                    pickAttachmentUrl(resAtt.file) ||
                    (response.attachment &&
                    response.attachment !== false &&
                    response.attachment.url
                        ? response.attachment.url
                        : null);
                setComments((prevComments) =>
                    prevComments.map((item) =>
                        item.id === messageId
                            ? {
                                  ...item,
                                  comment: trimmedMessage,
                                  image: updatedFile ?? item.image,
                                  attachments: {
                                      fileUrl:
                                          updatedFile ??
                                          item.attachments?.fileUrl,
                                      audioUrl:
                                          pickAttachmentUrl(resAtt.audio) ??
                                          item.attachments?.audioUrl,
                                      videoUrl:
                                          pickAttachmentUrl(resAtt.video) ??
                                          item.attachments?.videoUrl,
                                  },
                              }
                            : item
                    )
                );
                toast.success('Comentario actualizado con exito');
                return true;
            }

            toast.error(
                'No pudimos guardar los cambios. Intentalo nuevamente.'
            );
            return false;
        } catch (error) {
            console.error('Error al editar el comentario:', error);
            toast.error(
                'No pudimos guardar los cambios. Intentalo nuevamente.'
            );
            return false;
        } finally {
            setIsEditingMessage(false);
        }
    };

    const handleNote = (e) => {
        const value = parseInt(e.target.value, 10);
        if (value < 0) setNote(100);
        else if (value > 100) setNote(0);
        else setNote(value);
    };

    const handleRedo = (e) => setRedo(e.target.checked);

    const handleNavigate = () => {
        if (conversation?.student_id) {
            window.open(
                `../../profesor/alumno/${conversation.student_id}`,
                '_blank'
            );
        }
    };

    if (isLoading) {
        return <SimpleLoading />;
    }

    if (!conversation) {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                No se encontró la conversación
            </div>
        );
    }

    const student = {
        id: conversation.student_id,
        name: conversation.student_name || 'Estudiante',
        image_url: '',
        partner_id: conversation.student_id,
    };

    const valuesCorrection = {
        comment_teacher: '',
        grade: 0,
        date: null,
    };

    return (
        <>
            <BannerTask
                student={student}
                displayMode="mobile"
                submissionId={null}
            />
            <SpeedGraderWrapper ref={containerRef}>
                <ViewActivity
                    width={`${leftWidthPercentage}%`}
                    pointerEventsNone={pointerEventsNone}
                >
                    <TitleActivity>
                        <p>{conversation.resource_name || 'Actividad'}</p>
                    </TitleActivity>
                    {/* <TaskRender
                        isText={true}
                        isFile={false}
                        text="<p>Sin entrega asociada a esta conversación.</p>"
                    /> */}
                    <ImageWrapper>
                        <Img
                            src={withoutSubmission}
                            alt="Sin entrega asociada a esta conversación."
                            width="40%"
                        />
                    </ImageWrapper>
                </ViewActivity>
                <ResizableLine
                    backgroundColor={backgroundColor}
                    onMouseDown={handleMouseDown}
                >
                    <SimbolControl />
                </ResizableLine>
                <AsideContainer width={`${100 - leftWidthPercentage}%`}>
                    <BannerTask
                        student={student}
                        submissionId={null}
                        displayMode="desktop"
                    />
                    <TaskWrapper>
                        <StudentTaskWrapper>
                            <StudentTaskHeader
                                correctedSubmission={false}
                                isRedo={false}
                                redo={redo}
                                handleNavigate={handleNavigate}
                                old={false}
                                note={note}
                                history={[{ id: 1 }]}
                                idAssignment={1}
                                grade={0}
                                correctionWithCheck={false}
                                resourceId={conversation.resource_id}
                                studentId={conversation.student_id}
                                subjectId={1}
                                learningObject={1}
                            />
                            <Body>
                                <Content>
                                    <WrapperTitle>
                                        <Text
                                            fontSize="16px"
                                            color="#222222"
                                            fontWeight="bold"
                                        >
                                            Calificación
                                        </Text>
                                    </WrapperTitle>
                                    <CardCorrectTask
                                        formatDate=""
                                        correctionDate=""
                                        studentComment=""
                                        correctionWithCheck={false}
                                        check={check}
                                        setCheck={setCheck}
                                        correctedSubmission={false}
                                        isEdit={false}
                                        editSuccess={false}
                                        valuesCorrection={valuesCorrection}
                                        note={note}
                                        redo={redo}
                                        handleNote={handleNote}
                                        handleRedo={handleRedo}
                                        old={false}
                                        order={1}
                                    />
                                </Content>
                                <WrapperTitle>
                                    <Text
                                        fontSize="16px"
                                        color="#222222"
                                        fontWeight="bold"
                                    >
                                        Comentarios sobre la entrega
                                    </Text>
                                </WrapperTitle>
                                <Content pd>
                                    <CommentsChat
                                        comments={comments}
                                        composerVariant="media"
                                        handleSendAudio={handleChatSendAudio}
                                        handleSendVideo={handleChatSendVideo}
                                        handleSendFile={handleChatSendFile}
                                        mediaPickerResetKey={chatMediaReset}
                                        fileInputId="presentation-comments-room-chat"
                                        handleSendMessage={handleSendMessage}
                                        comment={comment}
                                        setComment={setComment}
                                        isLoading={isSendingMessage}
                                        canEditComment={(item) =>
                                            item.author_is_teacher === true &&
                                            item.is_legacy !== true
                                        }
                                        onEditMessage={handleEditMessage}
                                        isEditingMessage={isEditingMessage}
                                    />
                                </Content>
                            </Body>
                        </StudentTaskWrapper>
                    </TaskWrapper>
                </AsideContainer>
            </SpeedGraderWrapper>
        </>
    );
};

export default PresentationComments;

const SpeedGraderWrapper = styled(Card)`
    border: solid 1px #c4c4c4;
    display: flex;
    height: 85vh;
    box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
    border-radius: 20px;
    position: relative;

    @media (max-width: 1200px) {
        display: flex;
        flex-direction: column;
        height: 100%;
        gap: 24px;
        border: 0px;
        box-shadow: none;
    }
`;

const ViewActivity = styled.div`
    height: 100%;
    width: ${(props) => props.width};
    pointer-events: ${(props) => props.pointerEventsNone && 'none'};
    @media (max-width: 1200px) {
        width: calc(100% - 2px);
        border: 1px solid #c4c4c4;
        border-radius: 0px 0px 20px 20px;
        div {
            box-shadow: none;
        }
    }
`;

const ResizableLine = styled.button`
    height: 100%;
    width: 6px;
    min-width: 6px;
    background-color: ${(props) => props.backgroundColor};
    border-radius: 0px;
    cursor: col-resize;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const SimbolControl = styled.div`
    width: 100%;
    height: 2rem;
    background-color: #999;
`;

const TitleActivity = styled.div`
    color: #fff;
    background-color: #b31d15;
    border-bottom: solid 1px #c4c4c4;
    width: calc(100% - 72px);
    display: flex;
    justify-content: space-between;
    padding: 19px 36px;
    font-size: 1.1rem;
    align-items: center;
    position: relative;
`;

const ImageWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
`;

const AsideContainer = styled.div`
    height: 100%;
    width: ${(props) => props.width};
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    @media (max-width: 1200px) {
        width: calc(100% - 2px);
        border: 1px solid #c4c4c4;
        border-radius: 20px;
        margin-bottom: 1rem;
    }
`;

const TaskWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 12px;
    min-width: 0;
    height: calc(100% - 61px);

    @media (max-width: 1200px) {
        border-radius: 20px;
    }
`;

const StudentTaskWrapper = styled(Card)`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    min-width: 0;
    gap: 1.5rem;
    box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-color: #e9e9e9 transparent;
    @media (max-width: 1200px) {
        border-radius: 20px;
    }
`;

const Body = styled.div`
    padding: 0 1.5rem 1rem 1.5rem;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
    max-width: 100%;
    flex: ${(p) => (p.pd ? 1 : '0 0 auto')};
    padding-top: ${(p) => p.pd && '1.5rem'};
    padding-bottom: 1.5rem;
`;

const WrapperTitle = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;
